import { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { config } from '../config';
import './PaymentSuccess.css';

interface TicketDetail {
  id: string;
  ticketNumber: string;
  ticketType: string;
  eventType: string;
  amount: number;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  eventDate: string | null;
  createdAt: string;
}

interface LocationState {
  ticketIds?: string[];
  buyerName?: string;
  buyerEmail?: string;
  buyerPhone?: string;
  totalAmount?: number;
}

interface BuyerInfo {
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  totalAmount: number;
}

export function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;

  const [tickets, setTickets] = useState<TicketDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [confetti, setConfetti] = useState<{ id: number; x: number; color: string; delay: number; size: number }[]>([]);

  // Buyer info — populated from either React Router state (mock mode)
  // or URL query params (live PayU gateway redirect)
  const [buyerInfo, setBuyerInfo] = useState<BuyerInfo>({
    buyerName: state?.buyerName || '',
    buyerEmail: state?.buyerEmail || '',
    buyerPhone: state?.buyerPhone || '',
    totalAmount: state?.totalAmount || 0,
  });

  useEffect(() => {
    // Parse query parameters — used when redirected back from live PayGlocal/PayU gateway callback
    const queryParams = new URLSearchParams(window.location.search);
    const queryTicketIds = queryParams.get('ticketIds')?.split(',').filter(Boolean) || [];

    const ticketIds = state?.ticketIds?.length ? state.ticketIds : queryTicketIds;

    // Populate buyer info from URL query params if not already set via state (live gateway flow)
    const qName = queryParams.get('buyerName');
    if (!state?.buyerName && qName) {
      setBuyerInfo({
        buyerName: decodeURIComponent(qName),
        buyerEmail: decodeURIComponent(queryParams.get('buyerEmail') || ''),
        buyerPhone: decodeURIComponent(queryParams.get('buyerPhone') || ''),
        totalAmount: parseFloat(queryParams.get('totalAmount') || '0'),
      });
    }

    if (!ticketIds || ticketIds.length === 0) {
      // No ticket IDs at all — redirect back to booking
      navigate('/book-tickets');
      return;
    }

    // Clear cart from sessionStorage on successful purchase
    sessionStorage.removeItem('luminar_cart');

    // Launch confetti particles
    const particles = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: ['#a78bfa', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'][Math.floor(Math.random() * 5)],
      delay: Math.random() * 2,
      size: 6 + Math.random() * 8,
    }));
    setConfetti(particles);

    // Fetch ticket details
    const fetchTickets = async () => {
      try {
        const results = await Promise.all(
          ticketIds.map((id) =>
            fetch(`${config.API_BASE_URL}/tickets/${id}`).then((r) => r.json()),
          ),
        );
        setTickets(results);
      } catch (err) {
        console.error('Failed to fetch ticket details', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async (ticketId: string, ticketNumber: string) => {
    setDownloading((prev) => ({ ...prev, [ticketId]: true }));
    try {
      const res = await fetch(`${config.API_BASE_URL}/tickets/${ticketId}/download`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticketNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert('Download failed. Please try again.');
    } finally {
      setDownloading((prev) => ({ ...prev, [ticketId]: false }));
    }
  };

  const handleDownloadAll = async () => {
    for (const ticket of tickets) {
      await handleDownload(ticket.id, ticket.ticketNumber);
      await new Promise((r) => setTimeout(r, 300));
    }
  };

  const TICKET_COLORS: Record<string, string> = {
    premium: '#9b59b6',
    basic: '#3498db',
    normal: '#2ecc71',
  };

  return (
    <div className="ps-page">
      {/* Confetti */}
      <div className="ps-confetti" aria-hidden="true">
        {confetti.map((p) => (
          <div
            key={p.id}
            className="ps-confetti__piece"
            style={{
              left: `${p.x}%`,
              background: p.color,
              width: p.size,
              height: p.size,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Ambient orbs */}
      <div className="ps-bg">
        <div className="ps-bg__orb ps-bg__orb--1" />
        <div className="ps-bg__orb ps-bg__orb--2" />
      </div>

      <div className="ps-container">
        {/* Success hero */}
        <div className="ps-hero">
          <div className="ps-hero__ring">
            <div className="ps-hero__check">✓</div>
          </div>
          <h1 className="ps-hero__title">Payment <span className="ps-hero__accent">Successful!</span></h1>
          <p className="ps-hero__sub">
            Your tickets have been confirmed and will be sent to{' '}
            <strong>{buyerInfo.buyerEmail || '—'}</strong>.
          </p>
        </div>

        {/* Buyer summary card */}
        <div className="ps-buyer-card">
          <div className="ps-buyer-card__row">
            <span className="ps-buyer-card__label">Name</span>
            <span className="ps-buyer-card__value">{buyerInfo.buyerName || '—'}</span>
          </div>
          <div className="ps-buyer-card__row">
            <span className="ps-buyer-card__label">Email</span>
            <span className="ps-buyer-card__value">{buyerInfo.buyerEmail || '—'}</span>
          </div>
          <div className="ps-buyer-card__row">
            <span className="ps-buyer-card__label">Phone</span>
            <span className="ps-buyer-card__value">{buyerInfo.buyerPhone || '—'}</span>
          </div>
          <div className="ps-buyer-card__row">
            <span className="ps-buyer-card__label">Total Paid</span>
            <span className="ps-buyer-card__value ps-buyer-card__value--accent">
              {buyerInfo.totalAmount ? `₹${buyerInfo.totalAmount.toLocaleString('en-IN')}` : '—'}
            </span>
          </div>
        </div>

        {/* Tickets section */}
        <div className="ps-tickets-section">
          <div className="ps-tickets-header">
            <h2 className="ps-tickets-title">
              Your Tickets ({loading ? '…' : tickets.length})
            </h2>
            {tickets.length > 1 && !loading && (
              <button className="ps-download-all" onClick={handleDownloadAll}>
                ⬇ Download All
              </button>
            )}
          </div>

          {loading ? (
            <div className="ps-loading">
              <div className="ps-spinner" />
              <span>Fetching your tickets…</span>
            </div>
          ) : (
            <div className="ps-tickets-grid">
              {tickets.map((ticket, idx) => {
                const color = TICKET_COLORS[ticket.ticketType.toLowerCase()] || '#8b5cf6';
                const isDownloading = downloading[ticket.id];

                return (
                  <div
                    key={ticket.id}
                    className="ps-ticket-card"
                    style={{ '--tc-color': color } as React.CSSProperties}
                    data-testid={`ticket-card-${idx}`}
                  >
                    <div className="ps-ticket-card__stripe" style={{ background: color }} />
                    <div className="ps-ticket-card__body">
                      <div className="ps-ticket-card__top">
                        <div>
                          <span className="ps-ticket-card__badge" style={{ background: color }}>
                            {ticket.ticketType.toUpperCase()}
                          </span>
                          <div className="ps-ticket-card__event">
                            {ticket.eventType.toUpperCase()} Event
                          </div>
                        </div>
                        <div className="ps-ticket-card__num">{ticket.ticketNumber}</div>
                      </div>

                      <div className="ps-ticket-card__divider">
                        <div className="ps-ticket-card__dot ps-ticket-card__dot--l" />
                        <div className="ps-ticket-card__dashes" />
                        <div className="ps-ticket-card__dot ps-ticket-card__dot--r" />
                      </div>

                      <div className="ps-ticket-card__bottom">
                        <div className="ps-ticket-card__detail">
                          <span className="ps-ticket-card__detail-label">Holder</span>
                          <span className="ps-ticket-card__detail-val">{ticket.buyerName}</span>
                        </div>
                        <div className="ps-ticket-card__detail">
                          <span className="ps-ticket-card__detail-label">Amount</span>
                          <span className="ps-ticket-card__detail-val">₹{Number(ticket.amount).toFixed(2)}</span>
                        </div>
                        <div className="ps-ticket-card__detail">
                          <span className="ps-ticket-card__detail-label">📅 Event Date</span>
                          <span className="ps-ticket-card__detail-val">
                            {ticket.eventDate ? (
                              <>
                                {new Date(ticket.eventDate).toLocaleDateString('en-IN', {
                                  day: 'numeric', month: 'short', year: 'numeric',
                                })}
                                <span className="ps-ticket__event-time">
                                  {new Date(ticket.eventDate).toLocaleTimeString('en-IN', {
                                    hour: '2-digit', minute: '2-digit', hour12: true,
                                  })}
                                </span>
                              </>
                            ) : '—'}
                          </span>
                        </div>
                        <div className="ps-ticket-card__detail">
                          <span className="ps-ticket-card__detail-label">Purchased On</span>
                          <span className="ps-ticket-card__detail-val ps-ticket__purchased">
                            {new Date(ticket.createdAt).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short', year: 'numeric',
                            })}
                          </span>
                        </div>
                      </div>

                      <button
                        className="ps-download-btn"
                        onClick={() => handleDownload(ticket.id, ticket.ticketNumber)}
                        disabled={isDownloading}
                        id={`download-ticket-${ticket.id}`}
                      >
                        {isDownloading ? (
                          <><div className="ps-spinner ps-spinner--sm" /> Downloading…</>
                        ) : (
                          <>⬇ Download PDF</>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="ps-cta">
          <Link to="/" className="ps-cta__home">← Back to Home</Link>
          <Link to="/book-tickets" className="ps-cta__more">Book More Tickets</Link>
        </div>
      </div>
    </div>
  );
}
